import React, { useEffect, useState } from 'react';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  LinearProgress,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { processData, getProcessingStatus, getProcessingHistory } from '../store/slices/dataProcessingSlice';
import { fetchEntities } from '../store/slices/entitySlice';
import { showError, showSuccess, showInfo } from '../store/slices/toastSlice';
import { ProcessDataRequest } from '../types';

const DataProcessing: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isProcessing, status, history, isLoading } = useAppSelector((state) => state.dataProcessing);
  const { entities } = useAppSelector((state) => state.entities);

  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState<string>('');
  const [textData, setTextData] = useState<string>('');
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Set meta first to avoid conditional hook ordering issues
  usePageMeta('Data Processing – AskNow Admin', 'Process and monitor AI readiness of your knowledge bases in AskNow.');

  useEffect(() => {
    dispatch(getProcessingStatus());
    dispatch(getProcessingHistory());
    dispatch(fetchEntities());
  }, [dispatch]);

  // Load entity data when entity is selected
  useEffect(() => {
    if (selectedEntityId) {
      const selectedEntity = entities.find(e => e.entity_id === selectedEntityId);
      if (selectedEntity && selectedEntity.data) {
        setTextData(selectedEntity.data);
      } else {
        setTextData('');
      }
    }
  }, [selectedEntityId, entities]);

  // Auto-refresh status when processing
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (autoRefresh && isProcessing) {
      interval = setInterval(() => {
        dispatch(getProcessingStatus());
      }, 5000); // Refresh every 5 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, isProcessing, dispatch]);

  const handleProcessData = async () => {
    try {
      if (!selectedEntityId) {
        dispatch(showError({
          title: 'Entity Required',
          message: 'Please select an entity to process.',
          duration: 5000,
        }));
        return;
      }

      if (!textData.trim()) {
        dispatch(showError({
          title: 'Text Required',
          message: 'Please enter text data to process.',
          duration: 5000,
        }));
        return;
      }

      const request: ProcessDataRequest = {
        entityId: selectedEntityId,
        data: textData.trim()
      };



      await dispatch(processData(request)).unwrap();
      dispatch(showSuccess({
        title: 'Data Processing Started',
        message: 'Data processing has been initiated successfully.',
        duration: 5000,
      }));

      // Close settings dialog
      setSettingsDialogOpen(false);

      // Refresh status and history after processing starts
      setTimeout(() => {
        dispatch(getProcessingStatus());
        dispatch(getProcessingHistory());
      }, 2000);
    } catch (error: any) {
      dispatch(showError({
        title: 'Processing Failed',
        message: error.message || 'Failed to start data processing.',
        duration: 5000,
      }));
    }
  };

  const handleRefresh = () => {
    dispatch(getProcessingStatus());
    dispatch(getProcessingHistory());
    dispatch(showInfo({
      title: 'Refreshing',
      message: 'Data processing status is being refreshed...',
      duration: 2000,
    }));
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'in_progress':
        return <CircularProgress size={16} />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'in_progress':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 4,
        p: 3,
        backgroundColor: 'background.paper',
        borderRadius: 3,
        color: 'text.primary',
        border: '1px solid rgba(28, 233, 224, 0.25)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Data Processing
          </Typography>
          {isProcessing && (
            <Chip
              label="Processing"
              color="warning"
              icon={<CircularProgress size={16} />}
              sx={{ color: 'white', bgcolor: 'rgba(255, 255, 255, 0.2)' }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={isLoading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<SettingsIcon />}
            onClick={() => setSettingsDialogOpen(true)}
            disabled={isProcessing}
          >
            Process Data
          </Button>
        </Box>
      </Box>

      {/* Current Status */}
      {status && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Current Processing Status
            </Typography>
            {isProcessing ? (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Processing Progress
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {status.progress}% Complete
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={status.progress}
                  sx={{ height: 10, borderRadius: 5, mb: 2 }}
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" color="text.secondary">
                        Processed Entities
                      </Typography>
                      <Typography variant="h6">
                        {status.processed_entities} / {status.total_entities}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" color="text.secondary">
                        Current Entity
                      </Typography>
                      <Typography variant="h6" noWrap>
                        {status.current_entity || 'N/A'}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" color="text.secondary">
                        Started At
                      </Typography>
                      <Typography variant="h6">
                        {status.started_at ? new Date(status.started_at).toLocaleTimeString() : 'N/A'}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" color="text.secondary">
                        Time Remaining
                      </Typography>
                      <Typography variant="h6">
                        {status.estimated_time_remaining
                          ? `${Math.ceil(status.estimated_time_remaining / 60)} min`
                          : 'Calculating...'
                        }
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Alert severity="info">
                No data processing is currently running. Click "Process Data" to start processing.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Processing History */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <HistoryIcon />
            <Typography variant="h6">
              Processing History
            </Typography>
          </Box>

          {history.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Entities</TableCell>
                    <TableCell>Files</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Triggered By</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {new Date(item.started_at).toLocaleDateString()}
                        <br />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(item.started_at).toLocaleTimeString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(item.status)}
                          label={item.status}
                          color={getStatusColor(item.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{item.processed_entities}</TableCell>
                      <TableCell>{item.processed_files}</TableCell>
                      <TableCell>{item.processing_time}s</TableCell>
                      <TableCell>{item.triggered_by}</TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton size="small">
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              No processing history available. Start processing data to see history.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onClose={() => setSettingsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Data Processing Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              select
              fullWidth
              label="Select Entity to Process"
              value={selectedEntityId}
              onChange={(e) => setSelectedEntityId(e.target.value)}
              margin="normal"
              required
              error={!selectedEntityId}
              helperText={
                !selectedEntityId
                  ? "Please select an entity"
                  : selectedEntityId ? `Selected: ${entities.find(e => e.entity_id === selectedEntityId)?.name || 'Unknown'}` : ""
              }
            >
              {entities.map((entity) => (
                <MenuItem key={entity.entity_id} value={entity.entity_id}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <span>{entity.name || 'Unnamed Entity'}</span>
                    <Typography variant="caption" color="text.secondary">
                      {entity.data?.length || 0} chars
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                fullWidth
                label="Data to Process"
                value={textData}
                onChange={(e) => setTextData(e.target.value)}
                margin="normal"
                multiline
                rows={6}
                placeholder="Enter the text data you want to process, or select an entity to load its existing data..."
                required
                error={!textData.trim()}
                helperText={
                  !textData.trim()
                    ? "Please enter data to process"
                    : `${textData.length} characters • ${textData.split(' ').length} words`
                }
                inputProps={{
                  maxLength: 10000, // Limit to 10k characters
                }}
              />
              <Button
                variant="outlined"
                onClick={() => setTextData('')}
                disabled={!textData}
                sx={{ mt: 2, height: 40 }}
              >
                Clear
              </Button>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                />
              }
              label="Auto-refresh status during processing"
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleProcessData}
            variant="contained"
            disabled={isProcessing}
            startIcon={<PlayIcon />}
          >
            Start Processing
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataProcessing;
